/**
 * Storefront theme preference — Classic Amber + Light by default.
 * Customer-facing mode is LIGHT ONLY. Stored `"dark"` is coerced to `"light"`.
 * Ignores OS `prefers-color-scheme`; style (amber/blue) persists after Appearance changes.
 *
 * Lives in `constants` (shared layer) so application slices may import it
 * without depending on presentation (`theme/`).
 *
 * Storage shape (plain strings, not JSON):
 * - `rx-theme-style` → `classic-amber` | `blue` (legacy: `amber`, `premium-blue`)
 * - `rx-theme-mode`  → `light` (legacy `"dark"` is healed to `"light"`)
 */

import {
  THEME_VARIANT,
  type AppPaletteMode,
  type ThemeVariant,
} from "@/constants/theme-variant";

export const RX_THEME_STYLE_KEY = "rx-theme-style";
export const RX_THEME_MODE_KEY = "rx-theme-mode";

export const DEFAULT_THEME_STYLE: ThemeVariant = THEME_VARIANT.AMBER;
export const DEFAULT_THEME_MODE: AppPaletteMode = "light";

export type ThemePreference = {
  themeStyle: ThemeVariant;
  themeMode: AppPaletteMode;
};

/** Alias contract used by callers that prefer `themeKey` / `mode` naming. */
export type ResolvedThemePreference = {
  themeKey: ThemeVariant;
  mode: AppPaletteMode;
};

const REGISTERED_THEME_KEYS = new Set<string>(Object.values(THEME_VARIANT));

let hasWarnedInvalidTheme = false;

function warnInvalidThemeRecovery(): void {
  if (hasWarnedInvalidTheme) return;
  hasWarnedInvalidTheme = true;
  // Temporary startup diagnostic — corrupted preference self-healed.
  // eslint-disable-next-line no-console -- intentional one-shot recovery signal
  console.warn("[theme] invalid persisted theme; using classic-amber/light");
}

/** Test-only: reset one-shot warn latch. */
export function resetThemeRecoveryWarnLatchForTests(): void {
  hasWarnedInvalidTheme = false;
}

function isThemeVariant(value: unknown): value is ThemeVariant {
  return typeof value === "string" && REGISTERED_THEME_KEYS.has(value);
}

function isPaletteMode(value: unknown): value is AppPaletteMode {
  return value === "light" || value === "dark";
}

function isMigratableStyleId(value: unknown): boolean {
  return (
    value === "amber" ||
    value === "blue" ||
    value === "premium-blue" ||
    value === "classic-amber" ||
    isThemeVariant(value)
  );
}

function defaults(): ThemePreference {
  return { themeStyle: DEFAULT_THEME_STYLE, themeMode: DEFAULT_THEME_MODE };
}

function toResolved(pref: ThemePreference): ResolvedThemePreference {
  return { themeKey: pref.themeStyle, mode: pref.themeMode };
}

/**
 * Map persisted / legacy style ids → canonical ThemeVariant.
 * Unknown values → classic-amber.
 */
export function normalizeThemeStyle(value: unknown): ThemeVariant {
  if (isThemeVariant(value)) return value;
  if (value === "blue" || value === "premium-blue") return THEME_VARIANT.BLUE;
  if (value === "classic-amber" || value === "amber") return THEME_VARIANT.AMBER;
  return DEFAULT_THEME_STYLE;
}

/**
 * Production storefront is light-only. Any value, including legacy `"dark"`,
 * resolves to `"light"`.
 */
export function normalizeThemeMode(_value?: unknown): AppPaletteMode {
  return DEFAULT_THEME_MODE;
}

/** Rewrite leftover `rx-theme-mode=dark` to the canonical light value. */
function healPersistedDarkMode(
  storage: Pick<Storage, "getItem" | "setItem"> | null | undefined,
): void {
  if (!storage) return;
  const raw = readStorageItem(storage, RX_THEME_MODE_KEY);
  if (raw !== "dark") return;
  try {
    storage.setItem(RX_THEME_MODE_KEY, DEFAULT_THEME_MODE);
  } catch {
    // ignore
  }
}

/**
 * Coerce any unknown preference input into a guaranteed-safe preference.
 * Accepts null/undefined, legacy mode-only strings, style ids, and preference objects.
 */
export function resolveThemePreference(input?: unknown): ThemePreference {
  if (input == null || input === "") {
    return defaults();
  }

  // Legacy: entire preference was a bare mode string. Dark is coerced to light.
  if (input === "light" || input === "dark") {
    return { themeStyle: DEFAULT_THEME_STYLE, themeMode: DEFAULT_THEME_MODE };
  }

  if (typeof input === "string") {
    if (!isMigratableStyleId(input)) {
      warnInvalidThemeRecovery();
      return defaults();
    }
    return { themeStyle: normalizeThemeStyle(input), themeMode: DEFAULT_THEME_MODE };
  }

  if (typeof input === "object") {
    const record = input as Record<string, unknown>;
    const rawStyle =
      record.themeStyle ??
      record.themeKey ??
      record.themeVariant ??
      record.theme ??
      record.style ??
      record.variant;
    const rawMode = record.themeMode ?? record.mode ?? record.colorMode;

    const styleOk = rawStyle == null || isMigratableStyleId(rawStyle);
    const modeOk = rawMode == null || isPaletteMode(rawMode);

    if (!styleOk || !modeOk) {
      warnInvalidThemeRecovery();
      return defaults();
    }

    return {
      themeStyle: normalizeThemeStyle(rawStyle ?? DEFAULT_THEME_STYLE),
      themeMode: normalizeThemeMode(rawMode ?? DEFAULT_THEME_MODE),
    };
  }

  warnInvalidThemeRecovery();
  return defaults();
}

export function resolveThemePreferenceAlias(input?: unknown): ResolvedThemePreference {
  return toResolved(resolveThemePreference(input));
}

function readStorageItem(
  storage: Pick<Storage, "getItem">,
  key: string,
): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function clearStorageItem(
  storage: Pick<Storage, "removeItem"> | null | undefined,
  key: string,
): void {
  if (!storage || typeof storage.removeItem !== "function") return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Parse a stored localStorage value. Supports plain strings and accidental JSON.
 * On malformed JSON: clears the key and returns null (caller falls back to defaults).
 */
export function parsePersistedThemeValue(
  raw: string | null,
  options?: {
    storage?: Pick<Storage, "removeItem"> | null;
    key?: string;
  },
): unknown {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const looksJson =
    trimmed.startsWith("{") ||
    trimmed.startsWith("[") ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'));

  if (!looksJson) return trimmed;

  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    if (options?.storage && options.key) {
      clearStorageItem(options.storage, options.key);
    }
    warnInvalidThemeRecovery();
    return null;
  }
}

/** Sanitize Redux (or any) color-mode slice shape into a safe preference. */
export function sanitizeColorModeState(input: unknown): ThemePreference {
  if (input == null || typeof input !== "object") {
    if (input != null) warnInvalidThemeRecovery();
    return defaults();
  }
  return resolveThemePreference(input);
}

/**
 * Resolve preference for app startup.
 * Heals leftover `rx-theme-mode=dark` to `light`. Does not write style defaults
 * when keys are missing. Clears corrupted keys; never throws.
 */
export function loadThemePreference(
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null | undefined =
    typeof localStorage !== "undefined" ? localStorage : null,
): ThemePreference {
  if (!storage) {
    return defaults();
  }

  healPersistedDarkMode(storage);

  try {
    const savedStyleRaw = readStorageItem(storage, RX_THEME_STYLE_KEY);
    const savedModeRaw = readStorageItem(storage, RX_THEME_MODE_KEY);

    if (savedStyleRaw == null && savedModeRaw == null) {
      return defaults();
    }

    const parsedStyle = parsePersistedThemeValue(savedStyleRaw, {
      storage,
      key: RX_THEME_STYLE_KEY,
    });
    const parsedMode = parsePersistedThemeValue(savedModeRaw, {
      storage,
      key: RX_THEME_MODE_KEY,
    });

    // Combined JSON accidentally stored in the style key.
    if (parsedStyle != null && typeof parsedStyle === "object" && !Array.isArray(parsedStyle)) {
      const combined = resolveThemePreference(parsedStyle);
      if (typeof parsedMode === "string" && isPaletteMode(parsedMode)) {
        return {
          themeStyle: combined.themeStyle,
          themeMode: normalizeThemeMode(parsedMode),
        };
      }
      return combined;
    }

    // Legacy: style key held only "light"/"dark".
    if (parsedStyle === "light" || parsedStyle === "dark") {
      const modeFromStyle = parsedStyle;
      const mode =
        typeof parsedMode === "string" && isPaletteMode(parsedMode)
          ? parsedMode
          : modeFromStyle;
      return {
        themeStyle: DEFAULT_THEME_STYLE,
        themeMode: normalizeThemeMode(mode),
      };
    }

    const styleBefore = parsedStyle;
    const modeBefore = parsedMode;

    const styleInvalid = styleBefore != null && !isMigratableStyleId(styleBefore);
    const modeInvalid = modeBefore != null && !isPaletteMode(modeBefore);

    if (styleInvalid || modeInvalid) {
      warnInvalidThemeRecovery();
      if (styleInvalid) clearStorageItem(storage, RX_THEME_STYLE_KEY);
      if (modeInvalid) clearStorageItem(storage, RX_THEME_MODE_KEY);
      return defaults();
    }

    return {
      themeStyle: normalizeThemeStyle(styleBefore ?? DEFAULT_THEME_STYLE),
      themeMode: normalizeThemeMode(modeBefore ?? DEFAULT_THEME_MODE),
    };
  } catch {
    warnInvalidThemeRecovery();
    return defaults();
  }
}

/** Persist after an explicit Appearance settings change only. */
export function saveThemeStyle(
  style: ThemeVariant,
  storage: Pick<Storage, "setItem"> | null | undefined =
    typeof localStorage !== "undefined" ? localStorage : null,
): void {
  if (!storage) return;
  try {
    storage.setItem(RX_THEME_STYLE_KEY, normalizeThemeStyle(style));
  } catch {
    // ignore
  }
}

export function saveThemeMode(
  mode: AppPaletteMode,
  storage: Pick<Storage, "setItem"> | null | undefined =
    typeof localStorage !== "undefined" ? localStorage : null,
): void {
  if (!storage) return;
  try {
    storage.setItem(RX_THEME_MODE_KEY, normalizeThemeMode(mode));
  } catch {
    // ignore
  }
}
